import { CartLinesUpdateEvent } from '@shopify/events';

/*
 * Mie & Cire : bouton « Passer au pack » du tiroir et de la page panier (snippets/mc-lot-nudge.liquid).
 * Retire les sacs pris à l'unité, ajoute le ou les packs avec leur cadeau,
 * puis prévient Horizon, qui redessine le tiroir et la pastille du panier.
 * Après une erreur, la suite se décide sur l'état réel du panier (/cart.js), jamais sur une réponse perdue.
 */

const ROOT = window.Shopify?.routes?.root ?? '/';
const SOURCE = 'mie-cire-lots';
const TOAST_DURATION = 5000;
const NBSP = ' ';
const busyButtons = new WeakSet();
const toastTimers = new WeakMap();
let focusWatch = 0;

const MESSAGES = {
  working: 'Mise à jour du panier…',
  changed: `Votre panier vient de changer${NBSP}: vérifiez-le avant de passer en pack.`,
  rolledBack: 'Le passage en pack n’a pas pu se faire. Votre panier est inchangé.',
  lost: 'Le passage en pack n’a pas pu se faire et des sacs à l’unité ont été retirés de votre panier. Vérifiez-le avant de payer.',
  failed: 'Le panier n’a pas pu être mis à jour. Vérifiez vos articles.',
  done: 'Panier mis à jour.',
};

async function cartRequest(path, body) {
  const response = await fetch(`${ROOT}${path}`, {
    method: body ? 'POST' : 'GET',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await response.json().catch(() => null);
  if (!response.ok || !data) throw new Error(data?.description || data?.message || `HTTP ${response.status}`);
  return data;
}

const readCart = () => cartRequest('cart.js').catch(() => null);
const lineQuantity = (cart, key) => cart.items.find((item) => item.key === key)?.quantity ?? 0;
const variantQuantity = (cart, id) =>
  cart.items.reduce((sum, item) => sum + (item.variant_id === id ? item.quantity : 0), 0);

function cartSectionIds() {
  const ids = new Set();
  for (const element of document.querySelectorAll('cart-items-component[data-section-id]')) {
    ids.add(element.dataset.sectionId);
  }
  return [...ids];
}

/**
 * Prévient le tiroir, la page panier et la pastille. Sans `sections`, Horizon recharge lui-même le rendu.
 * `cart` peut manquer (relecture ratée) : la pastille lit alors `itemCount`.
 */
function notifyCartChanged(origin, cart, sections, key, quantity, itemCount = cart?.item_count) {
  origin.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [{ id: key, quantity }],
      promise: Promise.resolve({
        cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null,
        detail: { sections, items: cart?.items, itemCount, source: SOURCE, didError: false },
      }),
    })
  );
}

/** Bloque les lignes et le paiement pendant l'échange : Horizon vise les lignes par leur numéro, qui change en cours de route. */
function lockCartItems(button, dialog) {
  const cartItems = button.closest('cart-items-component') ?? (dialog ?? document).querySelector('cart-items-component');
  const rows = cartItems?.querySelector('.cart-items__wrapper');
  cartItems?.classList.add('cart-items-disabled');
  rows?.setAttribute('inert', '');
  return () => {
    cartItems?.classList.remove('cart-items-disabled');
    rows?.removeAttribute('inert');
  };
}

/** Un + / − tapé il y a moins de 300 ms n'est pas encore parti (délai d'Horizon) : on le repère à l'écart entre le champ et le panier. */
function hasUnsentQuantity(cart) {
  return cart.items.some((item) => {
    const input = document.getElementById(`CartItem-${item.key}`)?.querySelector('.cart-items__quantity input');
    return input && Number(input.value) !== item.quantity;
  });
}

/**
 * Message hors de la zone que Horizon redessine : il survit à la mise à jour et il est lu par les lecteurs d'écran.
 * Dans le tiroir (dialog modal), il doit rester dans le dialog ; sur la page panier, il est fixé en bas de l'écran.
 */
function getToast(dialog) {
  const host = dialog ?? document.body;
  let toast = host.querySelector(':scope > .mc-lot-toast');
  if (!toast) {
    toast = document.createElement('p');
    toast.className = dialog ? 'mc-lot-toast' : 'mc-lot-toast mc-lot-toast--page';
    toast.setAttribute('role', 'status');
    // Page panier : couche supérieure du navigateur, au-dessus de la bannière cookies et de la barre d'aperçu
    const topLayer = !dialog && typeof toast.showPopover === 'function';
    if (topLayer) toast.setAttribute('popover', 'manual');
    host.append(toast);
    if (topLayer) toast.showPopover();
  }
  return toast;
}

function showToast(dialog, message, tone) {
  const toast = getToast(dialog);
  clearTimeout(toastTimers.get(toast));
  toast.dataset.tone = tone;
  toast.textContent = message;
  toastTimers.set(
    toast,
    setTimeout(() => {
      toast.textContent = '';
    }, TOAST_DURATION)
  );
}

/**
 * Attend que la mise à jour retire le bouton, replace le focus s'il est tombé hors du tiroir,
 * puis appelle onDone (toujours, focus déplacé ou non).
 */
function focusAfterRender(button, dialog, lineKey, onDone, maxWait = 10000) {
  const watch = ++focusWatch; // un échange plus récent remplace la surveillance précédente
  const started = performance.now();
  const step = () => {
    if (watch !== focusWatch) return;
    if (button.isConnected && performance.now() - started < maxWait) {
      requestAnimationFrame(step);
      return;
    }
    const active = document.activeElement;
    const focusLost = !active || active === document.body || active === dialog;
    if (!button.isConnected && focusLost) {
      // Une requête par sélecteur, dans l'ordre de préférence ; sur la page panier, on cherche dans la page
      const scope = dialog ?? document;
      const row = lineKey ? document.getElementById(`CartItem-${lineKey}`) : null;
      const target =
        row?.querySelector('a.cart-items__title') ??
        scope.querySelector('a.cart-items__title') ??
        scope.querySelector('.cart-items__empty-button') ??
        dialog?.querySelector('.theme-drawer__close-button');
      target?.focus();
    }
    onDone?.();
  };
  requestAnimationFrame(step);
}

function setBusy(button, busy) {
  const nudge = button.closest('[data-mc-lot-nudge]');
  if (busy) {
    busyButtons.add(button);
    button.setAttribute('aria-disabled', 'true');
    button.setAttribute('aria-busy', 'true');
  } else {
    busyButtons.delete(button);
    button.removeAttribute('aria-disabled');
    button.removeAttribute('aria-busy');
  }
  nudge?.classList.toggle('is-loading', busy);
}

async function swapToLots(button) {
  let plan;
  try {
    plan = JSON.parse(button.dataset.mcLotSwap ?? '');
  } catch {
    return;
  }
  if (!plan?.key || !Array.isArray(plan.add) || plan.add.length === 0) return;

  const dialog = button.closest('dialog');
  const moved = plan.quantity - plan.keep;
  const lotsCount = plan.add.reduce((sum, lot) => sum + lot.quantity, 0);
  let unlock = () => {};
  let removed = false; // des sacs à l'unité ont quitté le panier sans que les lots y soient entrés
  setBusy(button, true);
  showToast(dialog, MESSAGES.working, 'info');

  // Après un échec : Horizon se recale sur le panier réel, et le focus revient dans le tiroir si l'encart disparaît
  const settleOnError = async (message) => {
    const cart = await readCart();
    if (cart) notifyCartChanged(button, cart, undefined, plan.key, lineQuantity(cart, plan.key));
    showToast(dialog, message, 'error');
    focusAfterRender(button, dialog, plan.key);
  };

  try {
    await Promise.allSettled(customElements.get('cart-items-component')?.pendingQuantityUpdates ?? []);
    // Verrou posé après l'attente : la fin d'une mise à jour Horizon retire cart-items-disabled
    unlock = lockCartItems(button, dialog);
    const before = await cartRequest('cart.js');
    const line = before.items.find((item) => item.key === plan.key);
    const unsent = hasUnsentQuantity(before);
    if (!line || line.quantity !== plan.quantity || unsent) {
      // Une saisie pas encore envoyée part d'elle-même et redessinera le tiroir : inutile de le recharger ici
      if (!unsent) {
        notifyCartChanged(button, before, undefined, plan.key, line?.quantity ?? 0);
        focusAfterRender(button, dialog, line ? plan.key : null);
      }
      showToast(dialog, MESSAGES.changed, 'error');
      return;
    }

    // 1. Retire les sacs à l'unité. Réponse perdue : on regarde si le retrait a bien eu lieu.
    try {
      await cartRequest('cart/change.js', { id: plan.key, quantity: plan.keep });
    } catch (error) {
      const now = await readCart();
      if (!now || lineQuantity(now, plan.key) !== plan.keep) throw error;
    }
    removed = true;

    // 2. Ajoute les lots, avec le rendu du tiroir dans la même réponse
    let added = null;
    try {
      added = await cartRequest('cart/add.js', {
        items: plan.add,
        sections: cartSectionIds().join(','),
        sections_url: window.location.pathname,
      });
    } catch (error) {
      console.warn('[mie-cire-lots]', error);
      const now = await readCart();
      const lotsAdded =
        now && plan.add.every((lot) => variantQuantity(now, lot.id) >= variantQuantity(before, lot.id) + lot.quantity);
      if (!lotsAdded) {
        // 3. Lots refusés (stock, réseau) : on remet les sacs, seulement s'il est sûr qu'aucun lot n'est entré
        const untouched =
          now && plan.add.every((lot) => variantQuantity(now, lot.id) === variantQuantity(before, lot.id));
        if (untouched) {
          try {
            await cartRequest('cart/add.js', {
              items: [{ id: plan.variant, quantity: moved, properties: line.properties ?? {} }],
            });
            removed = false;
          } catch {
            const check = await readCart();
            if (check && variantQuantity(check, plan.variant) >= variantQuantity(before, plan.variant)) removed = false;
          }
        }
        await settleOnError(removed ? MESSAGES.lost : MESSAGES.rolledBack);
        return;
      }
      // Shopify a bien ajouté les lots malgré l'erreur : on termine comme un succès, sans rendu fourni
    }
    removed = false;

    // Succès : la relecture ne sert qu'à la pastille ; si elle échoue, on garde le rendu reçu
    const cart = await readCart();
    const itemCount = cart?.item_count ?? before.item_count - moved + lotsCount;
    notifyCartChanged(button, cart, added?.sections, plan.key, plan.keep, itemCount);
    const doneMessage = button.dataset.mcLotDone || MESSAGES.done;
    // Annonce après le focus : sinon la lecture du lien focalisé coupe le message (NVDA, VoiceOver)
    focusAfterRender(
      button,
      dialog,
      added?.items?.[0]?.key ?? null,
      () => setTimeout(() => showToast(dialog, doneMessage, 'success'), 250),
      2000
    );
  } catch (error) {
    console.warn('[mie-cire-lots]', error);
    await settleOnError(removed ? MESSAGES.lost : MESSAGES.failed);
  } finally {
    unlock();
    setBusy(button, false);
  }
}

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-mc-lot-swap]') : null;
  if (!(button instanceof HTMLButtonElement) || busyButtons.has(button)) return;
  event.preventDefault();
  swapToLots(button);
});

// Crée la zone de message dès le chargement : une zone « status » créée au moment du message n'est pas toujours lue
const drawerDialog = document.querySelector('#cart-drawer dialog');
if (drawerDialog) getToast(drawerDialog);
else if (document.querySelector('cart-items-component:not([data-drawer])')) getToast(null);
